import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../firebase/AuthContext';
import './Login.css';

const Login = () => {
  const navigate = useNavigate();
  const { signIn, signInWithGoogle, handleRedirectResult } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // エラーメッセージを非表示にする
    setShowError(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setShowError(false);
    setErrorMessage('');
    
    // 簡単なバリデーション
    if (!formData.email || !formData.password) {
      setShowError(true);
      setErrorMessage('メールアドレスとパスワードを入力してください');
      setIsLoading(false);
      return;
    }
    
    try {
      // Firebase認証でログイン
      const user = await signIn(formData.email, formData.password);
      // ログイン成功
      
      // メール認証チェック
      if (user && !user.emailVerified) {
        // メール認証が未完了
        // メール認証画面に遷移
        navigate('/email-verification', { 
          state: { 
            email: formData.email,
            from: '/top' 
          } 
        });
        return;
      }
      
      // ログイン成功後、Top画面に遷移
      navigate('/top');
    } catch (error) {
      console.error('ログインエラー:', error);
      setShowError(true);
      
      // 退会済みアカウントのエラーメッセージをチェック
      if (error.message && error.message.includes('退会済み')) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('メールアドレスまたはパスワードに誤りがあります');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async (useRedirect = false) => {
    setIsLoading(true);
    setShowError(false);
    setErrorMessage('');
    
    try {
      // Googleログインを開始
      const result = await signInWithGoogle(useRedirect);
      
      if (useRedirect) {
        // リダイレクトの場合は結果をここでは処理しない
        return;
      }
      
      if (result) {
        // Googleログイン成功
        
        // プロフィール設定が必要かチェック
        if (result.needsProfileSetup) {
          // プロフィール設定が必要です
          navigate('/profile-setup');
        } else {
          // プロフィール設定済み、Topページに遷移
          navigate('/top');
        }
      }
    } catch (error) {
      console.error('Googleログインエラー:', error);
      setShowError(true);
      
      // ポップアップがブロックされた場合、リダイレクト方式を提案
      if (error.message && error.message.includes('ポップアップ')) {
        setErrorMessage(
          <div>
            <p>{error.message}</p>
            <p style={{fontSize: '0.85rem', color: '#666', marginBottom: '10px'}}>
              ポップアップがブロックされている場合は、ブラウザの設定を確認するか、下記ボタンで別の方式をお試しください。
            </p>
            <button 
              onClick={() => handleGoogleLogin(true)}
              style={{
                marginTop: '5px',
                padding: '8px 16px',
                backgroundColor: '#04c5cf',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              別の方式で再試行
            </button>
          </div>
        );
      } else if (error.message && error.message.includes('キャンセル')) {
        setErrorMessage('ログインがキャンセルされました。');
      } else if (error.message && error.message.includes('認証ドメイン')) {
        setErrorMessage('認証設定に問題があります。管理者にお問い合わせください。');
      } else if (error.message && error.message.includes('退会済み')) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage(`Googleログインに失敗しました: ${error.message || 'ネットワークエラー'}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = (e) => {
    e.preventDefault();
    navigate('/forgot-password');
  };

  const handleBackToStart = () => {
    navigate('/');
  };

  // リダイレクト結果の処理
  useEffect(() => {
    const processRedirectResult = async () => {
      try {
        const result = await handleRedirectResult();
        if (result) {
          // リダイレクト結果を処理
          
          if (result.needsProfileSetup) {
            navigate('/profile-setup');
          } else {
            navigate('/top');
          }
        }
      } catch (error) {
        console.error('リダイレクト結果の処理エラー:', error);
        setShowError(true);
        setErrorMessage('ログイン処理中にエラーが発生しました。');
      }
    };

    processRedirectResult();
  }, [handleRedirectResult, navigate]);

  return (
    <div className="login-page-container">
      <div className="login-container">
        <img src="/img/tiny-identity.png" alt="Tiny identity" className="login-logo" />
        <h1 className="login-title">Tiny identity</h1>
        
        {/* エラーメッセージ - 既存デザインに影響しないインラインスタイル */}
        {showError && (
          <div style={{
            color: '#e74c3c',
            backgroundColor: '#fdf2f2',
            border: '1px solid #fecaca',
            borderRadius: '6px',
            padding: '10px',
            marginBottom: '20px',
            fontSize: '0.9rem'
          }}>
            {errorMessage}
          </div>
        )}
        
        {/* ログインフォーム本体 */}
        <form className="login-form" onSubmit={handleSubmit}>
          <label htmlFor="email">メールアドレス</label>
          <input 
            type="email" 
            id="email" 
            name="email" 
            value={formData.email}
            onChange={handleInputChange}
            disabled={isLoading}
            required
          />

          <label htmlFor="password">パスワード</label>
          <input 
            type="password" 
            id="password" 
            name="password" 
            value={formData.password}
            onChange={handleInputChange}
            disabled={isLoading}
            required
          />

          <button 
            type="submit" 
            className="btn-primary" 
            disabled={isLoading}
            style={{
              opacity: isLoading ? 0.6 : 1,
              cursor: isLoading ? 'not-allowed' : 'pointer'
            }}
          >
            {isLoading ? 'ログイン中...' : 'ログイン'}
          </button>

          <a 
            className="login-footer-link" 
            href="#" 
            onClick={handleForgotPassword}
          >
            パスワードをわすれた方はこちら
          </a>

          <button
            type="button"
            className="btn-cancel"
            onClick={handleBackToStart}
            disabled={isLoading}
            style={{
              opacity: isLoading ? 0.6 : 1,
              cursor: isLoading ? 'not-allowed' : 'pointer'
            }}
          >
            キャンセル
          </button>
        </form>

        <button
          type="button"
          className="gsi-material-button"
          onClick={() => handleGoogleLogin(false)}
          disabled={isLoading}
        >
          {isLoading ? (
            <div>認証中...</div>
          ) : (
            <>
              <div className="gsi-material-button-state"></div>
              <div className="gsi-material-button-content-wrapper">
                <div className="gsi-material-button-icon">
                  <svg
                    version="1.1"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 48 48"
                    xmlnsXlink="http://www.w3.org/1999/xlink"
                    style={{ display: 'block' }}
                  >
                    <path
                      fill="#EA4335"
                      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                    />
                    <path
                      fill="#4285F4"
                      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                    />
                    <path
                      fill="#34A853"
                      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                    />
                    <path fill="none" d="M0 0h48v48H0z" />
                  </svg>
                </div>
                <span className="gsi-material-button-contents">Sign in with Google</span>
                <span style={{ display: 'none' }}>Sign in with Google</span>
              </div>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default Login;
